/**
 * 宿主挂载 —— 编辑器持有真实 <svg>，Vue 只提供宿主 div（PLAN §3.1 边界 1）。
 *
 * 尺寸策略：**不改文档里的 width/height 属性**。外层 .sve-viewport 用 viewBox 的
 * 宽高撑成 px（滚动区即由它决定），内层 svg 由 CSS 撑满 100%。
 * 于是屏幕上的尺寸与"文件内容"彻底解耦，导出无需还原任何东西。
 */
import { clampZoom, viewBoxRect, viewTransform, type ViewState } from '@/core/geom/viewport';

export interface MountHandle {
  readonly host: HTMLElement;
  readonly viewport: HTMLElement;
  svg: SVGSVGElement | null;
  readonly view: ViewState;
  /** 把视图（缩放/平移）写进容器 CSS */
  setView(next: Partial<ViewState>): void;
  /** 容器尺寸变化后重新贴合（不碰文档） */
  relayout(): void;
  destroy(): void;
}

export function createMount(host: HTMLElement, initial: ViewState = { zoom: 1, panX: 0, panY: 0 }): MountHandle {
  const viewport = document.createElement('div');
  viewport.className = 'sve-viewport';
  host.appendChild(viewport);

  const view: ViewState = { zoom: clampZoom(initial.zoom), panX: initial.panX, panY: initial.panY };
  let svg: SVGSVGElement | null = null;

  const relayout = (): void => {
    if (!svg) return;
    const box = viewBoxRect(svg);
    if (!box) return;
    viewport.style.width = `${box.w}px`;
    viewport.style.height = `${box.h}px`;
    viewport.style.transform = viewTransform(view);
  };

  const setView = (next: Partial<ViewState>): void => {
    Object.assign(view, next);
    view.zoom = clampZoom(view.zoom);
    if (!Number.isFinite(view.panX)) view.panX = 0;
    if (!Number.isFinite(view.panY)) view.panY = 0;
    viewport.style.transform = viewTransform(view);
  };

  return {
    host,
    viewport,
    get svg() {
      return svg;
    },
    set svg(next: SVGSVGElement | null) {
      if (svg && svg.parentNode === viewport) viewport.removeChild(svg);
      svg = next;
      if (next) {
        // ★ 绝不往文档元素上写任何东西（class/style 都会落进导出文件）
        //   尺寸与样式一律由外层 .sve-viewport 的 CSS 规则控制
        viewport.appendChild(next);
        relayout();
      }
    },
    view,
    setView,
    relayout,
    destroy() {
      if (svg && svg.parentNode === viewport) viewport.removeChild(svg);
      svg = null;
      if (viewport.parentNode === host) host.removeChild(viewport);
    },
  };
}
